import {
	MailIcon,
	PhoneIcon,
	UserIcon,
	CakeIcon,
	ClockIcon,
} from "@heroicons/react/outline";
import { useSingleItem } from "../../hooks/useFetch";
import { NavLink, Route, Switch, useLocation } from "react-router-dom";
import Role from "../../shared/Role";
import { IUser } from "../../types/userType";
import { formatDate2 } from "../../utils/wordUtils";
import ProfileTable from "admin/src/components/Table/User/ProfileTable";
import UserStatus from "./UserStatus";
import UserProduct from "./UserProduct";
import LogsTable from "admin/src/components/Table/LogsTable";

type NavLinkType = {
	name: string;
	description: string;
};

type MainType = {
	id: string | number;
	name: string;
	icon: React.ReactNode;
};

const navLinks: NavLinkType[] = [
	{ name: "Info", description: "Detailed information" },
	{ name: "Status", description: "User status" },
	{ name: "Products", description: "User Listing" },
	{ name: "Logs", description: "Timeline information" },
];

const UserDetail = () => {
	//react current path
	const { pathname } = useLocation();
	const [user, loading] = useSingleItem<IUser>(pathname.split("/")[3], "user");

	const main: MainType[] = [
		{
			id: 0,
			name: `${user.email}` || "-",
			icon: <MailIcon className="h-4 w-4" />,
		},
		{
			id: 1,
			name: `+62 ${user.profile?.phone}` || "-",
			icon: <PhoneIcon className="h-4 w-4" />,
		},
		{
			id: 2,
			name: user.profile?.gender || "male",
			icon: <UserIcon className="h-4 w-4" />,
		},
		{ id: 3, name: "1 May 2000", icon: <CakeIcon className="h-4 w-4" /> },
		{
			id: 4,
			name: `Since ${formatDate2(user.createdAt || "")}` || "-",
			icon: <ClockIcon className="h-4 w-4" />,
		},
	];
	return (
		<div className="mt-10 shadow-lg py-10 px-10 bg-white rounded-xl w-full h-full">
			<div id="head" className="flex gapx-10 h-full  ">
				<div
					id="user-avatar"
					className={`relative bg-white w-80 h-80 rounded-lg mr-10 flex-shrink-0 inline-flex items-center justify-center text-neutral-100 uppercase font-semibold shadow-inner ${
						loading && "animate-pulse bg-gray-200"
					}`}
				>
					{loading || (
						<img
							src={user.profile?.avatar || ""}
							className={`absolute inset-0 w-full h-full object-cover rounded-lg`}
							alt=".."
						></img>
					)}

					{user.verified && (
						<span
							className={` bg-sky-500 h-10 w-10 rounded-full text-white text-xs flex items-center justify-center absolute -top-4 -right-4 shadow-md border-4 border-white`}
						>
							<i className="fas fa-check "></i>
						</span>
					)}
				</div>
				<div className="flex flex-col align-middle justify-center text-slate-600">
					<h2 className="font-bold first-letter:uppercase text-4xl">
						{user.profile?.displayName || "-"}
					</h2>
					<p className=" text-opacity-80">{user.profile?.fullName || "-"}</p>
					<div className="flex items-center text-xs mt-3 text-slate-700 gap-x-3">
						{user.admin && <Role type="admin" />}
						{user.products?.length! > 0 && <Role type="vendor" />}
					</div>
					<div id="property" className="grid gap-y-2 pt-8">
						{main.map((v) => {
							return (
								<div
									className="flex items-center gap-x-3 text-slate-500 text-xs"
									key={v.id}
								>
									{" "}
									{v.icon}
									<p>{v.name}</p>
								</div>
							);
						})}
					</div>
				</div>
			</div>
			<div className="bg-white overflow-hidden sm:rounded-lg w-full h-full">
				<nav className="my-6">
					<ul className="grid grid-cols-4">
						{navLinks.map((li) => {
							return (
								<NavLink
									key={li.name}
									to={`${li.name.toLowerCase()}`}
									className="px-4 py-5 sm:px-6 opacity-50 hover:bg-sky-50"
									activeClassName="border-b-2  border-sky-500 px-4 py-5 sm:px-6"
								>
									<h3 className="text-lg leading-6 font-medium text-gray-900">
										{li.name}
									</h3>
									<p className="mt-1 max-w-2xl text-sm text-gray-500">
										{li.description}
									</p>
								</NavLink>
							);
						})}
					</ul>
				</nav>
				<Switch>
					<Route
						path="/dashboard/user/:id/info"
						children={<ProfileTable context={user} />}
					/>
					<Route
						path="/dashboard/user/:id/status"
						children={<UserStatus context={user} />}
					/>
					<Route
						path="/dashboard/user/:id/products"
						children={<UserProduct context={user} />}
					/>
					<Route
						path="/dashboard/user/:id/logs"
						children={<LogsTable context={user} />}
					/>
				</Switch>
			</div>

			<div></div>
		</div>
	);
};

export default UserDetail;
